<script setup lang="ts">
import LinePlus from '@docue/docue-ui-v2/icons/LinePlus.vue'
import LineList from '@docue/docue-ui-v2/icons/LineList.vue'
import LineClock from '@docue/docue-ui-v2/icons/LineClock.vue'
import LineHome01 from '@docue/docue-ui-v2/icons/LineHome01.vue'
import LineFolder from '@docue/docue-ui-v2/icons/LineFolder.vue'
import LineScales01 from '@docue/docue-ui-v2/icons/LineScales01.vue'
import LineBookOpen01 from '@docue/docue-ui-v2/icons/LineBookOpen01.vue'
import LineChevronDown from '@docue/docue-ui-v2/icons/LineChevronDown.vue'
import LineLayerSingle from '@docue/docue-ui-v2/icons/LineLayerSingle.vue'
import LineChevronRight from '@docue/docue-ui-v2/icons/LineChevronRight.vue'
import LineLayersThree01 from '@docue/docue-ui-v2/icons/LineLayersThree01.vue'
import LineArrowNarrowUpRight from '@docue/docue-ui-v2/icons/LineArrowNarrowUpRight.vue'

const { isNewArchiveEnabled: hasNewArchive } = useAuth()
const { data: config } = useQueryConfig.current()
const { t } = useI18n()
const { hasFeature } = useFeatureAccess()
const { hasValidSubscription } = useSubscription()

const hasFormsService = computed(() => hasFeature('forms-service'))

const isViewsEnabled = computed(() =>
  hasValidSubscription.value && hasFeature('archive-custom-views'),
)
const archivePath = computed(() => hasNewArchive.value ? '/2/documents' : '/documents')

const route = useRoute()

const { data: viewsData } = useQueryViews.all({
  fieldsToInclude: ['filters'],
})

const views = computed(() => viewsData.value?.data ?? [])

const viewsExpanded = useLocalStorage('app-navigation-views-expanded', () => false)

const isTemplateDetailRoute = computed(() => route.path.startsWith('/2/template/'))
const isSignDocumentRoute = computed(() => route.path.startsWith('/2/sign'))
const { isTouchDevice } = useTouch()

const signPDFDropzone = useTemplateRef('signPDFDropzone')
const filesForSigning = useFilesForSigning()

const handleFileDrop = async (files: File[] | null) => {
  if (!files)
    return

  filesForSigning.value = files
  await nextTick()
  await navigateTo({ name: '2-sign' })
}

const { isOverDropZone } = useDropZone(signPDFDropzone, {
  onDrop: handleFileDrop,
  dataTypes: types => isSignDocumentRoute.value ? false : types.every(type => type === 'application/pdf'),
  multiple: true,
})

watch(() => route.params, (params) => {
  if ('viewId' in params && params.viewId)
    viewsExpanded.value = true
}, { immediate: true })
</script>

<template>
  <nav>
    <DTNavList class="gap-4">
      <DTNavList class="my-2 gap-2 md:gap-4">
        <li>
          <UiButton
            color="primary"
            as-child
            class="w-full shrink-0"
          >
            <NuxtLink :to="{ name: '2-templates' }">
              <UiButtonIcon
                name="docue:file-plus-01"
                size="md"
              />
              {{ t('navigation.draft') }}
            </NuxtLink>
          </UiButton>
        </li>

        <li>
          <UiButton
            v-if="isTouchDevice || !hasNewArchive"
            as-child
            color="neutral"
            variant="outline"
            class="w-full shrink-0"
          >
            <NuxtLink :to="{ name: '2-sign' }">
              <UiButtonIcon
                name="docue:signature"
                size="md"
                class="shrink-0"
              />
              {{ t('navigation.sign') }}
            </NuxtLink>
          </UiButton>

          <div
            v-else
            ref="signPDFDropzone"
            class="relative flex flex-col items-center justify-center gap-1 rounded-lg px-4 py-6 ring-1 ring-inset ring-transparent"
            :class="{
              '!ring-gray-800': isOverDropZone && !isSignDocumentRoute,
              [$style.dropzoneBorder]: !isSignDocumentRoute,
              'bg-gray-50 hover:ring-gray-300': !isSignDocumentRoute,
              'bg-blue-50 text-blue-800': isSignDocumentRoute,
            }"
          >
            <span
              aria-hidden="true"
              class="contents"
            >
              <UiIcon
                name="docue:upload-cloud-01"
                size="1.5rem"
              />
              <span class="text-center text-sm font-medium">{{ t('navigation.signing-action') }}…</span>
            </span>
            <NuxtLink
              :to="{ name: '2-sign' }"
              class="absolute inset-0 rounded-lg"
            >
              <span class="sr-only">{{ t('navigation.signing-action') }}…</span>
            </NuxtLink>
          </div>
        </li>
      </DTNavList>

      <DTNavList>
        <DTNavItem
          v-if="hasNewArchive"
          to="/2/home"
          :active="route.path.startsWith('/2/home')"
          variant="light-blue"
          class="whitespace-nowrap"
          component-class="!text-[0.8125rem]"
        >
          <DTNavItemIcon>
            <LineHome01 class="size-full stroke-2" />
          </DTNavItemIcon>
          {{ t('navigation.home') }}
        </DTNavItem>
        <DTNavItem
          :to="{ name: '2-templates' }"
          :active="isTemplateDetailRoute || route.path.startsWith('/2/templates')"
          variant="light-blue"
          class="whitespace-nowrap"
          component-class="!text-[0.8125rem]"
        >
          <DTNavItemIcon>
            <IconTemplatePen class="size-full" />
          </DTNavItemIcon>
          {{ t('navigation.templates') }}
        </DTNavItem>
        <DTNavItem
          :to="archivePath"
          :active="route.path === '/2/documents' || route.path.startsWith('/2/documents/f/')"
          variant="light-blue"
          class="whitespace-nowrap"
          component-class="!text-[0.8125rem]"
        >
          <DTNavItemIcon>
            <LineFolder class="size-full" />
          </DTNavItemIcon>
          {{ hasNewArchive ? t('documents.root-title') : t('navigation.all-documents') }}
        </DTNavItem>
        <DTNavItem
          v-if="hasNewArchive"
          to="/2/documents/v/recent"
          :active="route.path.startsWith('/2/documents/v/recent')"
          variant="light-blue"
          class="whitespace-nowrap"
          component-class="!text-[0.8125rem]"
        >
          <DTNavItemIcon>
            <LineClock class="size-full" />
          </DTNavItemIcon>
          {{ t('navigation.documents.recent') }}
        </DTNavItem>
        <DTNavItem
          :to="hasNewArchive ? { name: '2-documents-v-finalized' } : { path: '/documents/finalized', external: true }"
          :active="route.path.startsWith('/2/documents/v/finalized') || route.path.startsWith('/documents/finalized')"
          variant="light-blue"
          class="whitespace-nowrap"
          component-class="!text-[0.8125rem]"
        >
          <DTNavItemIcon>
            <UiIcon
              name="docue:signature"
              class="size-full"
            />
          </DTNavItemIcon>
          {{ t('navigation.documents.finalized') }}
        </DTNavItem>
        <template v-if="hasNewArchive">
          <li
            class="flex list-none flex-col gap-1 whitespace-nowrap"
          >
            <div
              class="flex items-center gap-0.5 rounded-lg pr-1 hover:bg-blue-25"
            >
              <button
                :aria-expanded="viewsExpanded"
                aria-controls="documents-view-list"
                class="
                    flex
                    grow
                    cursor-default
                    items-center
                    gap-3
                    rounded-lg
                    px-3
                    py-1.5
                    text-sm
                    text-gray-700
                    "
                @click="viewsExpanded = !viewsExpanded"
              >
                <DTNavItemIcon>
                  <LineLayersThree01 class="size-full" />
                </DTNavItemIcon>
                <span class="flex items-center gap-1">
                  <span class="text-[0.8125rem]">{{ t('navigation.views.title') }}</span>
                  <span
                    class="flex size-4 items-center justify-center"
                  >
                    <LineChevronDown
                      v-if="viewsExpanded"
                      class="size-3 [&>path]:stroke-[2.5]"
                    />
                    <LineChevronRight
                      v-else
                      class="size-3 [&>path]:stroke-[2.5]"
                    />
                  </span>
                </span>
              </button>
              <NuxtLink
                v-if="isViewsEnabled"
                v-dt-tooltip.dark="t('navigation.views.create')"
                to="/2/documents/v/new"
                class="
                    flex
                    size-6
                    items-center
                    justify-center
                    rounded-lg
                    text-gray-400
                    hover:bg-blue-50
                    hover:text-blue-800
                    "
              >
                <LinePlus class="size-4" />
                <span class="sr-only">{{ t('navigation.views.create') }}</span>
              </NuxtLink>
            </div>
            <DTNavList
              v-show="viewsExpanded"
              id="documents-view-list"
              :aria-hidden="!viewsExpanded"
              class="ml-4"
            >
              <DTLoaderDots v-if="!views" />
              <span
                v-else-if="!views.length"
                class="pl-6 text-xs text-gray-500"
              >{{ t('navigation.views.no-views') }}</span>
              <template v-else>
                <DTNavItem
                  v-for="view in views"
                  :key="view.id"
                  :to="`/2/documents/v/${view.id}`"
                  :active="route?.params?.viewId === view.id"
                  variant="light-blue"
                  :title="view.title"
                  component-class="w-full"
                >
                  <DTNavItemIcon class="shrink-0">
                    <LineLayerSingle class="size-full" />
                  </DTNavItemIcon>
                  <div class="truncate whitespace-nowrap text-[0.8125rem]">
                    {{ view.title }}
                  </div>
                </DTNavItem>
              </template>
            </DTNavList>
          </li>
        </template>
        <DTNavItem
          :to="{ name: '2-contacts' }"
          :active="route.name?.toString().startsWith('2-contacts')"
          variant="light-blue"
          class="whitespace-nowrap"
          component-class="!text-[0.8125rem]"
        >
          <DTNavItemIcon>
            <LineBookOpen01 class="size-full" />
          </DTNavItemIcon>
          {{ t('navigation.contacts') }}
        </DTNavItem>
        <DTNavItem
          v-if="hasFormsService"
          to="https://forms.docue.com/admin/"
          variant="light-blue"
          class="whitespace-nowrap"
          component-class="!text-[0.8125rem]"
          @click.prevent.stop="navigateTo(
            'https://forms.docue.com/admin/', { external: true, open: { target: '_blank' } },
          )"
        >
          <DTNavItemIcon>
            <LineList class="size-full" />
          </DTNavItemIcon>
          <span class="grow text-left">{{ t('navigation.forms') }}</span>
          <DTNavItemIcon>
            <LineArrowNarrowUpRight class="size-full stroke-2 text-gray-400" />
          </DTNavItemIcon>
        </DTNavItem>
        <!-- TODO: remove click-handlers when DTNavItem supports target property DEV-6144 -->
        <DTNavItem
          v-if="config?.link_legal_articles"
          :to="config.link_legal_articles"
          class="whitespace-nowrap"
          variant="light-blue"
          component-class="!text-[0.8125rem]"
          @click.prevent.stop="navigateTo(
            config.link_legal_articles, { external: true, open: { target: '_blank' } },
          )"
        >
          <DTNavItemIcon>
            <LineScales01 class="size-full stroke-2" />
          </DTNavItemIcon>
          <span class="grow text-left">{{ t('navigation.legal-hub') }}</span>
          <DTNavItemIcon>
            <LineArrowNarrowUpRight class="size-full stroke-2 text-gray-400" />
          </DTNavItemIcon>
        </DTNavItem>
      </DTNavList>
    </DTNavList>
  </nav>
</template>

<style module>
.dropzoneBorder {
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23D6D3D1FF' stroke-width='1' stroke-dasharray='8%2c 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
}
</style>
